import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/internationalization/client/x-tel-input.js';
import '@adornis/popover/x-dropdown';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { DateTime } from 'luxon';
import { BHInvoice } from '../../entities/BHInvoice.js';
import type { BHTranslationDictionary } from '../../translations/translation.js';
import { ikhGenerateInvoice, IKHInvoice, sendIKHInvoiceMails } from '../payment/ikh-payment-plugin.js';
import type { InvoiceUserInvoiceTablePlugin } from '../plugin-types.js';

export const ikhInvoiceUserInvoicesTable: InvoiceUserInvoiceTablePlugin = {
  id: 'invoice-user-invoice-table::ikh',
  type: 'invoice-user-invoice-table',
  render: (
    host: ChemistryLitElement,
    translation: TranslationController<BHTranslationDictionary>,
    userID: string,
    institutionID: string,
  ) => {
    return html`
      <x-flex space="md">
        <tr-h3> ${translation.translate('user', 'invoices')} </tr-h3>
        <tr-data-view
          .coloring=${host.colors.accent.shade(300)}
          .options=${{
            class: BHInvoice,
            isEntryClickable: false,
            isSortable: true,
            view: new View({
              filter: new AdornisFilter({
                aspects: [
                  new StringAspect({
                    mode: 'equals',
                    fieldName: 'userID',
                    value: userID,
                  }),
                  new StringAspect({
                    mode: 'equals',
                    fieldName: 'institutionID',
                    value: institutionID,
                  }),
                ],
              }),
            }),
            customColumns: [
              {
                fieldName: 'courses',
                columnName: 'Veranstaltung',
                selectionSet: {
                  ...selectionSet(() => BHInvoice, 4),
                  institutionID: 1,
                  userID: 1,
                  participations: {
                    _id: 1,

                    product: { _id: 1, name: 1, appointments: { _id: 1, start: 1 } },
                  },
                },
                renderItem: instance => {
                  console.log({ instance: instance.payment });
                  return instance.payment.participations
                    .map(participation => participation.product?.name ?? '')
                    .join(', ');
                },
              },
              {
                fieldName: 'created',
                columnName: 'Erstellt',
              },
              {
                fieldName: 'sentDate',
                columnName: 'Versandt',
              },
              {
                customID: '_previewInvoice',
                columnName: 'Rechnung',
                renderItem: instance => {
                  return html`
                    <x-flex vertical space="xs">
                      <x-flex space="xs" wrap horizontal>
                        <x-button
                          icon="visibility"
                          .color=${host.colors.accent.shade(300)}
                          mode="text"
                          @click=${() => goTo(AdornisFile.getServeLink(instance.pdfID), { target: '_blank' })}
                          >ansehen</x-button
                        >
                      </x-flex>

                      <!-- korrektur -->
                      ${(() => {
                        if (!instance.pdfCorrectionInternalID) return nothing;
                        return html`
                          <x-flex space="xs" wrap horizontal>
                            <x-button
                              icon="visibility"
                              .color=${host.colors.accent.shade(300)}
                              mode="text"
                              @click=${() =>
                                goTo(AdornisFile.getServeLink(instance.pdfCorrectionID), { target: '_blank' })}
                              >ansehen (Korrektur)</x-button
                            >
                          </x-flex>
                        `;
                      })()}
                    </x-flex>
                  `;
                },
              },
              {
                customID: '_previewInvoiceAccounting',
                columnName: 'Rechnung (Buchhaltung)',
                renderItem: instance => {
                  if (!instance.pdfInternalID) return html`-------`;
                  return html`
                    <x-flex vertical space="xs">
                      <x-flex space="xs" wrap horizontal>
                        <x-button
                          icon="visibility"
                          .color=${host.colors.accent.shade(300)}
                          mode="text"
                          @click=${() => goTo(AdornisFile.getServeLink(instance.pdfInternalID), { target: '_blank' })}
                          >ansehen</x-button
                        >
                      </x-flex>

                      <!-- korrektur -->
                      ${(() => {
                        if (!instance.pdfCorrectionInternalID) return nothing;
                        return html`
                          <x-flex space="xs" wrap horizontal>
                            <x-button
                              icon="visibility"
                              .color=${host.colors.accent.shade(300)}
                              mode="text"
                              @click=${() =>
                                goTo(AdornisFile.getServeLink(instance.pdfCorrectionInternalID), { target: '_blank' })}
                              >ansehen (Korrektur)</x-button
                            >
                          </x-flex>
                        `;
                      })()}
                    </x-flex>
                  `;
                },
              },
              {
                customID: '_options',
                columnName: '',
                renderHeader: _ => html`
                  <x-flex
                    space="xs"
                    horizontal
                    center
                    crossaxis-center
                    w100
                    ${acss({
                      color: host.colors.tone.success.shade(800),
                      'tr-icon': { fontSize: `${host.baseFontSize}px` },
                      'tr-text': { textWrap: 'nowrap' },
                    })}
                  >
                    <tr-icon>electric_bolt</tr-icon>
                    <tr-text bold></tr-text>
                  </x-flex>
                `,
                renderItem: instance => {
                  console.log({ invoice: instance });
                  const institutionID = instance.payment.participations[0]?.product?.institutionID;
                  if (!institutionID) throw new Error('could not get institutionID');
                  const reference = createRef<XDropdown>();
                  return html` <x-flex horizontal space-between center crossaxis-center>
                    <tr-button-small
                      icon="more_horiz"
                      mode="outline"
                      @click=${async () => {
                        reference.value?.toggle();
                      }}
                    ></tr-button-small>
                    <x-dropdown ${ref(reference)}>
                      <x-flex slot="dropdown-content">
                        <x-card>
                          <x-flex vertical crossaxis-start ${css({ gap: '14px' })}>
                            <!-- neu generieren -->
                            <x-button
                              mode="text"
                              class="hover-text-button"
                              icon="autorenew"
                              @click=${async () => {
                                await XNativeDialog.waitFor(ikhGenerateInvoice(instance.paymentID, false)());
                                await XSnackbar.show('Die Rechnung wurde neu ausgestellt');
                              }}
                            >
                              Rechnung neu generieren
                            </x-button>

                            <!-- Rechnungskorrektur generieren -->
                            <x-button
                              mode="text"
                              class="hover-text-button"
                              icon="autorenew"
                              @click=${async () => {
                                await XNativeDialog.waitFor(ikhGenerateInvoice(instance.paymentID, true)());
                                await XSnackbar.show('Die Rechnungskorrektur wurde neu ausgestellt');
                              }}
                            >
                              RechnungsKorrektur generieren
                            </x-button>

                            <x-hr></x-hr>

                            <!-- Rechnung senden -->
                            <x-button
                              mode="text"
                              class="hover-text-button"
                              icon="send"
                              @click=${async () => {
                                const success = await XNativeDialog.waitFor(sendIKHInvoiceMails(instance._id, false)());
                                if (success) {
                                  instance.sentDate = DateTime.now();
                                  await instance.save();
                                  XSnackbar.show('E-Mail wurde versendet');
                                } else {
                                  XSnackbar.show('es gab einen Fehler beim versenden der E-Mail');
                                }
                              }}
                            >
                              Rechnung senden
                            </x-button>

                            <!-- Rechnungs Korrektur senden -->
                            <x-button
                              mode="text"
                              class="hover-text-button"
                              icon="send"
                              @click=${async () => {
                                const success = await XNativeDialog.waitFor(sendIKHInvoiceMails(instance._id, true)());
                                if (success) {
                                  instance.sentDate = DateTime.now();
                                  await instance.save();
                                  XSnackbar.show('E-Mail wurde versendet');
                                } else {
                                  XSnackbar.show('es gab einen Fehler beim versenden der E-Mail');
                                }
                              }}
                            >
                              Rechnungs-Korrektur senden
                            </x-button>
                          </x-flex>
                        </x-card>
                      </x-flex>
                    </x-dropdown>
                  </x-flex>`;
                },
                columnWidth: 'min-content',
              },
            ],
          } satisfies DataViewOptions<typeof IKHInvoice>}
        ></tr-data-view>
      </x-flex>
    `;
  },
};
